import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import FormErrorMessage from "@ui-kit/form/FormErrorMessage/FormErrorMessage.web";
import PinInput from "@ui-kit/form/PinInput/PinInput.web";
import PinInputField from "@ui-kit/form/PinInputField/PinInputField.web";
import Button from "@ui-kit/form/Button/Button.web";
import Form from "@ui-kit/form/Form/Form.web";
import Text from "@ui-kit/typography/Text/Text.web";
import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import FormLabel from "@ui-kit/form/FormLabel/FormLabel.web";
import { useForm } from "react-hook-form";
import { useTranslation } from "@shared/utils/internationalization";
import { useState } from "react";
import { addSecondsToDate } from "@shared/utils/dates/arithmetic";
import { Timer, TimerProvider, useTimer } from "@ui-kit/feedback/timer";
import type { OtpFormSharedProps } from "./OtpForm.props";

export type OtpFormProps = OtpFormSharedProps & {
  // Add any web specific props here.
};

// the number of seconds that an OTP code is valid
export const DEFAULT_OTP_STEP = 30;

export default function OtpForm({
  onVerifyOtp,
  onOtpResend,
  email,
}: OtpFormProps): JSX.Element {
  const { t } = useTranslation("auth");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [isInvalidOtp, setIsInvalidOtp] = useState(false);

  const [otpStep, setOtpStep] = useState<Date>(
    addSecondsToDate(new Date(), DEFAULT_OTP_STEP)
  );
  const [timeRemaining, setTimeRemaining] = useTimer(otpStep);

  const schema = yup.object({
    otp: yup
      .string()
      .length(6, t("Verification code must be 6 digits"))
      .required(t("Verification code is required")),
  });

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: { otp: "" },
    mode: "onSubmit",
    resolver: yupResolver(schema),
  });

  const otpValue = watch("otp");

  const verifyOtp = async (otp: string) => {
    if (onVerifyOtp) {
      await onVerifyOtp(otp);
    }
    // TODO: Implement the default behavior for self-contained OTP verification
  };

  const onSubmit = async (data: { otp: string }) => {
    setIsInvalidOtp(false);
    try {
      if (data.otp) {
        const { otp } = data;
        setIsSubmitting(true);
        await verifyOtp(otp);
      }
    } catch (error) {
      setIsSubmitting(false);
      setIsInvalidOtp(true);
      reset({ otp: "" }); // Reset the OTP fields with an empty string when the code is invalid
    }
  };

  const resendCode = async () => {
    setIsResendingCode(true);
    if (onOtpResend) {
      const newExpiry = await onOtpResend();
      const expiryDate = addSecondsToDate(
        new Date(),
        newExpiry ?? DEFAULT_OTP_STEP
      );
      setOtpStep(expiryDate);
      setTimeRemaining(expiryDate);
    } else {
      // TODO: Implement the default behavior for self-contained OTP resend
    }
    setIsResendingCode(false);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Text
        textStyle={{ base: "heading.3xl", sm: "heading.5xl" }}
        as="h1"
        mb="2xl"
      >
        {t("Verify your account")}
      </Text>
      <Text mb="2xl" wordBreak="break-word">
        {t("Enter the 6-digit verification code sent to")} {email}
      </Text>
      <FormControl isInvalid={Boolean(errors.otp)} mb="2xl">
        <FormLabel fontSize="sm" htmlFor="otp">
          {t("Email verification code")}
        </FormLabel>
        <PinInput
          value={otpValue || ""}
          otp
          onChange={(value) => setValue("otp", value)}
          isRequired
          fullWidth
          autoFocus
          isInvalid={isInvalidOtp}
          size="md"
          onComplete={(value) => {
            if (!isSubmitting) {
              onSubmit({ otp: value });
            }
          }}
        >
          {Array.from({ length: 6 }, (_, index) => (
            <PinInputField key={`pin-digit-${index + 1}`} />
          ))}
        </PinInput>
        <FormErrorMessage>{errors.otp?.message}</FormErrorMessage>
      </FormControl>
      <Button
        data-testid="otp-form-verify"
        mb="3xl"
        width="100%"
        variant="solid"
        colorPalette="primary"
        isLoading={isSubmitting}
        type="submit"
      >
        {t("Verify")}
      </Button>
      <Text mb="2xl" color="fg">
        {t(
          "Can't find your code? Check your spam folder! Or request a new code."
        )}
      </Text>
      <TimerProvider>
        <Button
          data-testid="otp-form-resend-code"
          width="100%"
          variant="link"
          colorPalette="tertiary"
          isLoading={isResendingCode}
          isDisabled={timeRemaining > 0}
          onClick={resendCode}
        >
          {timeRemaining > 0 ? (
            <Text>
              {t("Resend available in")} <Timer targetDate={otpStep} />
            </Text>
          ) : (
            <Text>{t("Resend code")}</Text>
          )}
        </Button>
      </TimerProvider>
    </Form>
  );
}
